import { loadProject, getAssetOrUndefined } from 'owa-timestream-store';
import { logUsage } from 'owa-analytics';
import type { ProjectId, OutlookSpaceAssetId, TimeStreamAsset } from 'owa-timestream-types';
import { trace } from 'owa-trace';
import getSdsSecondaryKey from '../substrate/getSdsSecondaryKey';

export default function handleAssetRowNotification(
    eventType: 'RowAdded' | 'RowModified',
    projectId: ProjectId,
    itemId: string,
    itemLastModified: string
) {
    const tracePrefix = `ASSET NOTIFICATION: ${eventType} ${projectId} ${itemId}`;

    getSdsSecondaryKey(projectId, itemId)
        .then(assetId => {
            if (assetId) {
                const asset = getAssetOrUndefined(projectId, assetId as OutlookSpaceAssetId);

                if (shouldReload(eventType, asset, itemLastModified)) {
                    trace.info(`${tracePrefix} ${assetId}: reload`);
                    loadProject(projectId);
                } else {
                    trace.info(`${tracePrefix} ${assetId}: no reload`);
                }
            } else {
                trace.info(`${tracePrefix}: no assetId`);
            }
        })
        .catch(err => {
            const msg = JSON.stringify(err);
            trace.warn(`ASSET NOTIFICATION FAILED: ${eventType} ${projectId} ${itemId} ${msg}`);
            logUsage('OUTLOOKSPACES_ASSET_NOTIFICATION_FAILED', [eventType, msg]);
        });
}

function shouldReload(
    eventType: 'RowAdded' | 'RowModified',
    asset: TimeStreamAsset | undefined,
    itemLastModified: string
) {
    if (!asset) {
        // If an asset was added or modified and we don't have it, reload.
        // Ideally the notification would carry enough data for us to perform this without reload.
        // See https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/99650
        return true;
    }

    if (eventType == 'RowAdded') {
        // No need to reload since we already have the new asset, ie, we're the one that added it.
        return false;
    }

    // For RowModified, only reload if we're not the one that did the modification.
    // Worst case is that we get the notification before we get the response from the web service call.
    const ourLastModified = new Date(asset.lastModifiedDateTime).getTime();
    const theirLastModified = new Date(itemLastModified).getTime();
    const shouldReload = ourLastModified < theirLastModified;
    return shouldReload;
}
